import { useLockedAction } from "@components/plan-subscription-lock";
import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Spinner from "@components/ui/spinner";
import Switch from "@components/ui/switch";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import {
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import {
  useCreateFolderMutation,
  useGetCurrentUserQuery,
} from "@/shared/services";
import { StoriesFolderUserPermEnum } from "@/shared/types";

const DIALOG_ID = "create-folder-dialog";

// Validation constants
const MIN_FOLDER_NAME_LENGTH = 1;
const MAX_FOLDER_NAME_LENGTH = 100;

export const CreateFolderDialog = ({ folderId }: { folderId: string }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: currentUser } = useGetCurrentUserQuery();

  const { plan } = useLockedAction({
    feature: "stories-folders-limit",
  });

  const [isOpenAccess, setIsOpenAccess] = useState(true);

  const { mutateAsync: createFolder, isPending: isLoading } =
    useCreateFolderMutation();

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Memoized validation
  const validation = useMemo(() => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return { isValid: false, error: "" };
    }
    if (trimmedName.length < MIN_FOLDER_NAME_LENGTH) {
      return {
        isValid: false,
        error: "Folder name must be at least 1 character",
      };
    }
    if (trimmedName.length > MAX_FOLDER_NAME_LENGTH) {
      return {
        isValid: false,
        error: `Folder name must be less than ${MAX_FOLDER_NAME_LENGTH} characters`,
      };
    }
    return { isValid: true, error: "" };
  }, [name]);

  // Memoized form data
  const formData = useMemo(() => {
    if (!currentUser || !validation.isValid) return null;

    return {
      name: name.trim(),
      openAccess: isOpenAccess,
      // null, never "": the tree builders read "" as neither root nor child.
      parentId: folderId || null,
      users: [
        {
          oktaId: currentUser.oktaId,
          status: StoriesFolderUserPermEnum.Owner,
        },
      ],
    };
  }, [name, isOpenAccess, currentUser, validation.isValid, folderId]);

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!formData || isLoading) return;

    setError("");

    try {
      await createFolder(formData);
      track("folder created successfully", {
        folderName: formData.name,
        openAccess: formData.openAccess,
      });
      toast.success("Folder created successfully!");
      handleClose();
    } catch (e: any) {
      const errorMessage =
        e?.data?.message || e?.message || "Error creating new folder";
      setError(errorMessage);

      track("error creating new folder", {
        newFolderName: formData.name,
        error: e,
        errorMessage,
      });

      toast.error(errorMessage);
    }
  }, [formData, isLoading, createFolder, handleClose]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter" && validation.isValid) {
        handleSubmit();
      }
    },
    [handleSubmit, validation.isValid],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
      // Clear error when user starts typing
      if (error) setError("");
    },
    [error],
  );

  const handleOpenAccessChange = useCallback((checked: boolean) => {
    setIsOpenAccess(checked);
  }, []);

  return (
    <Card className="tw-w-[468px]">
      <CardHeader className="tw-flex tw-items-center tw-justify-between">
        <CardTitle>New folder</CardTitle>
        <CardCloseButton onClick={handleClose} />
      </CardHeader>
      <CardContent>
        <div className="tw-space-y-2">
          <Input
            ref={inputRef}
            placeholder="Add your folder name here..."
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            className={cn(
              "tw-h-9 tw-rounded-lg tw-px-3 tw-font-sans tw-text-base tw-font-normal",
              error && "tw-focus:tw-border-red-500 tw-border-red-500",
            )}
            aria-invalid={!!error}
            aria-describedby={error ? "folder-name-error" : undefined}
            maxLength={MAX_FOLDER_NAME_LENGTH}
          />

          {error && (
            <p
              id="folder-name-error"
              className="tw-font-sans tw-text-sm tw-text-red-600"
              role="alert"
            >
              {error}
            </p>
          )}

          {name.length > MAX_FOLDER_NAME_LENGTH * 0.8 && (
            <p className="tw-text-gray-500 tw-font-sans tw-text-xs">
              {name.length}/{MAX_FOLDER_NAME_LENGTH} characters
            </p>
          )}
        </div>

        {plan === "team" && (
          <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-blue-50 tw-px-4 tw-py-3">
            <span className="tw-font-sans tw-text-base tw-font-normal tw-text-blue">
              Share with everyone in your organization
            </span>
            <Switch.Toggle
              size="md"
              checked={isOpenAccess}
              onCheckedChange={handleOpenAccessChange}
              aria-label="Share with everyone in your organization"
            />
          </div>
        )}
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-4">
        <Button
          variant="secondary"
          className="tw-font-sans tw-text-sm tw-font-medium"
          onClick={handleClose}
          disabled={isLoading}
        >
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!validation.isValid}
          className={cn(
            "tw-font-sans tw-text-sm tw-font-medium",
            isLoading && "tw-cursor-not-allowed",
          )}
          aria-describedby={error ? "folder-name-error" : undefined}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Creating...
            </>
          ) : (
            "Create folder"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

CreateFolderDialog.id = DIALOG_ID;
